import { Upload } from "@mui/icons-material";
import { ButtonView, Plugin } from "ckeditor5";
import ReactDOMServer from "react-dom/server";

type UploadResponse = {
  default: string;
};

export type UploadHandler = (file: File) => Promise<UploadResponse>;

export default class MediaUploadPlugin extends Plugin {
  static get pluginName(): string {
    return "MediaUploadPlugin";
  }

  init(): void {
    const { editor } = this;
    const uploadHandler = editor.config.get("mediaUpload.uploadHandler") as UploadHandler;

    editor.ui.componentFactory.add("uploadMedia", (locale) => {
      const view = new ButtonView(locale);
      const svgString = ReactDOMServer.renderToStaticMarkup(<Upload />);

      view.set({
        icon: svgString.replace("<svg", '<svg xmlns="http://www.w3.org/2000/svg"'),
        tooltip: "Загрузить медиа",
        withText: false,
      });

      view.on("execute", () => this.openFileDialog(uploadHandler));

      return view;
    });

    this.enableDragAndDrop(uploadHandler);
  }

  private openFileDialog(uploadHandler: UploadHandler): void {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*,video/*,audio/*";
    input.multiple = true;
    input.click();

    input.onchange = async () => {
      if (!input.files?.length) return;
      for (let i = 0; i < input.files.length; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await this.uploadAndInsert(input.files[i], uploadHandler);
      }
    };
  }

  private enableDragAndDrop(uploadHandler: UploadHandler) {
    const { editor } = this;
    const viewDocument = editor.editing.view.document;

    this.listenTo(viewDocument, "drop", async (evt, data) => {
      const { dataTransfer } = data;
      if (!dataTransfer || !dataTransfer.files.length) return;

      data.preventDefault();
      data.stopPropagation();

      if (data.domEvent) {
        data.domEvent.preventDefault();
        data.domEvent.stopPropagation();
      }

      for (let i = 0; i < dataTransfer.files.length; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await this.uploadAndInsert(dataTransfer.files[i], uploadHandler);
      }
    });
  }

  private async uploadAndInsert(file: File, uploadHandler: UploadHandler) {
    const { editor } = this;
    const response = await uploadHandler(file);
    const url = response.default;
    if (!url) return;

    if (file.type.startsWith("image")) {
      editor.execute("imageInsert", { source: url });
    } else if (file.type.startsWith("video")) {
      editor.execute("insertVideo", { src: url });
    } else if (file.type.startsWith("audio")) {
      editor.execute("insertAudio", { src: url });
    }
  }
}
